import { TooltipRenderProps } from 'react-joyride'
import { Trans, useTranslation } from 'next-i18next'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import Button from '~/components/Buttons/Button'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import CloseButtonIcon from '~/components/Icons/svgs/CloseButtonIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { selectTutorialProduct, setTutorialStep, setShouldShowTutorial } from '~/redux/slices/dashboard'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import HexPlayButtonIcon from '~/components/Icons/svgs/HexPlayButtonIcon'
import HexActivityButtonIcon from '~/components/Icons/svgs/HexActivityButtonIcon'
import avatarTutorialAnalytics from '~/analytics/avatar/avatarTutorialAnalytics'
import { selectSubjectLabel } from '~/curriculum/slice/curriculum.slice'
import PuzzlePiece from '~/curriculum/components/PuzzlePiece'

import { TutorialPlacement, TutorialProducts } from '../types/dashboard.types'

const mediaQuery = [{ screen: '(min-width: 240px)', width: '52px' }]
const mediaQueryIcon = [{ screen: '(min-width: 240px)', width: '38px' }]

const JoyRideTooltip = (cProps: TooltipRenderProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const product = useAppSelector(selectTutorialProduct)
  const subjectLabel = useAppSelector(selectSubjectLabel)

  const { index, primaryProps, skipProps, size, step, tooltipProps } = cProps
  const { arrowPlacement, arrowPosition, icon, emoji } = step
  const { t } = useTranslation('dashboard')
  const title = `${product}.step${index + 1}.title`
  const action = `${product}.step${index + 1}.action`
  const { onClick: onSkipClick, ...restSkipProps } = skipProps
  const { onClick: onPrimaryClick, ...restPrimaryProps } = primaryProps
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })

  const onNextClick = (e: React.MouseEvent<HTMLElement>): void => {
    // log event for next tutorial step
    if (product === TutorialProducts.AVATAR_TUTORIAL) {
      avatarTutorialAnalytics.logAvatarTour({ step: index + 1, finished: index + 1 === size ? 1 : 0 })
    } else if (product === TutorialProducts.MATH_TASK) {
      // learningPathTutorialAnalytics.logMathTaskCardTour({ step: index + 1, finished: index + 1 === size ? 1 : 0 })
    }

    onPrimaryClick(e)
  }
  const onCloseClick = (e: React.MouseEvent<HTMLElement>): void => {
    // log event for closing tutorial
    if (product === TutorialProducts.AVATAR_TUTORIAL) {
      dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
    } else if (product === TutorialProducts.MATH_TASK) {
      // learningPathTutorialAnalytics.logMathTaskCardTour({ step: index + 1, out: 1 })
    }

    dispatch(setTutorialStep(0))
    dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
    onSkipClick(e)
  }
  return (
    <div
      {...tooltipProps}
      className=" w-80 bg-white rounded border-2 border-purple text-black-light-fade rounded-lg text-center"
    >
      {arrowPlacement === TutorialPlacement.TOP && (
        <div className="relative">
          <div
            className={`absolute left-0 right-0 -top-3.5 w-6 h-6 ${
              isMobile && arrowPosition === 'left' ? 'ml-4' : 'ml-auto'
            } mr-auto bg-white  border-purple border-t-2 border-l-2 -bottom-3.5 rotate-45`}
          />
        </div>
      )}
      <div className="flex justify-between text-purple px-2 pt-1">
        <div className="text-purple font-semibold text-base">
          {t('step')} {index + 1}/{size}
        </div>
        <Button {...restSkipProps} onClick={onCloseClick}>
          <SVGIcon icon={CloseButtonIcon} pathClassName="text-purple" size={18} />
        </Button>
      </div>
      <div className="px-4 pb-4">
        {icon.length > 0 && (
          <div className="flex justify-center">
            {icon === 'hexagon' && (
              <ResponsiveImage
                alt="hexagon icon"
                height={42}
                imageClassName=""
                imageWidths="38"
                mediaQuery={mediaQueryIcon}
                srcKey="dashboard/hexagon.png"
                width={38}
              />
            )}
          </div>
        )}
        <div className="text-base font-semibold leading-8 inline-block">
          {Trans({
            t,
            i18nKey: title,
            components: {
              span: <span className="text-orchid-dark" />,
              CapImage: (
                <span className="inline-flex items-center gap-1">
                  {' '}
                  <ResponsiveImage
                    alt="Black Cap Image"
                    height={272}
                    imageClassName="w-6"
                    imageWidths="24"
                    srcKey="curriculum/hats/black-hat-numbers-and-quantities.png"
                    width={447}
                  />
                </span>
              ),
              PuzzleImage: (
                <span className="relative top-1 inline-flex items-center gap-1">
                  <PuzzlePiece size={21} />
                </span>
              ),
              Chest: (
                <span className="relative top-1 inline-flex items-center gap-1">
                  {' '}
                  <ResponsiveImage
                    alt="Black Cap Image"
                    height={452}
                    imageClassName="w-6"
                    imageWidths="26"
                    srcKey="onboarding/icons/updated-chest-purple.png"
                    width={648}
                  />
                </span>
              ),
            },
            children: t(title),
            values: { subjectLabel },
          })}
        </div>
        {emoji.length > 0 && (
          <div className="flex justify-center py-2">
            {emoji === 'fantastic' && (
              <ResponsiveImage
                alt="Learning path emoji"
                height={52}
                imageClassName="w-13"
                imageWidths="52"
                mediaQuery={mediaQuery}
                srcKey="dashboard/emoji_fantastick_3d.png"
                width={52}
              />
            )}
            {emoji === 'playAction' && (
              <div className="flex gap-4">
                <SVGIcon icon={HexPlayButtonIcon} size={85} />
                <SVGIcon icon={HexActivityButtonIcon} size={85} />
              </div>
            )}
          </div>
        )}
        <Button
          {...restPrimaryProps}
          className="mt-2 inline-flex  bg-purple  text-white border"
          noPadding
          onClick={onNextClick}
          size="small"
        >
          <span className=" w-32 py-1.5 text-base font-semibold">{t(action)}</span>
        </Button>
      </div>

      {arrowPlacement === TutorialPlacement.BOTTOM && (
        <div className="relative">
          <div
            className={`absolute left-0 right-0 w-6 h-6 ${
              isMobile && arrowPosition === 'left' ? 'ml-24' : 'ml-auto'
            } mr-auto bg-white  border-purple border-b-2 border-r-2 -bottom-3.5 rotate-45`}
          />
        </div>
      )}
    </div>
  )
}

export default JoyRideTooltip
